/**
 * DateRangeFilter.tsx - Date Range Filter Component
 *
 * Provides quick select buttons and custom date range picker.
 * Validates date ranges and prevents future dates.
 */

import React, { useState, useEffect } from 'react';
import { VStack, HStack, Button, Input, Text, Box } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { DateRangeFilter as DateRangeFilterType } from '@/types/filter.types';

interface DateRangeFilterProps {
    value: DateRangeFilterType;
    onChange: (value: DateRangeFilterType) => void;
}

export const DateRangeFilter: React.FC<DateRangeFilterProps> = ({ value, onChange }) => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir() === 'rtl';

    const [customFrom, setCustomFrom] = useState<string>(value.customFrom || '');
    const [customTo, setCustomTo] = useState<string>(value.customTo || '');
    const [error, setError] = useState<string>('');

    useEffect(() => {
        setCustomFrom(value.customFrom || '');
        setCustomTo(value.customTo || '');
    }, [value.customFrom, value.customTo]);


    const validateAndApplyCustomRange = () => {
        setError('');

        if (!customFrom && !customTo) {
            // Both empty, clear the filter
            onChange({
                customFrom: null,
                customTo: null,
            });
            return;
        }

        const today = new Date();
        today.setHours(23, 59, 59, 999); // End of today

        const fromDate = customFrom ? new Date(customFrom) : null;
        const toDate = customTo ? new Date(customTo) : null;

        // Validate future dates
        if (fromDate && fromDate > today) {
            setError(t('filter_no_future_dates') || 'Cannot select future dates');
            return;
        }

        if (toDate && toDate > today) {
            setError(t('filter_no_future_dates') || 'Cannot select future dates');
            return;
        }

        // Validate from < to
        if (fromDate && toDate && fromDate > toDate) {
            setError(t('filter_from_before_to') || 'From date must be before To date');
            return;
        }

        // Valid range
        onChange({
            customFrom: customFrom || null,
            customTo: customTo || null,
        });
    };

    const handleCustomFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCustomFrom(e.target.value);
        setError('');
    };

    const handleCustomToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCustomTo(e.target.value);
        setError('');
    };

    const handleCustomFromBlur = () => {
        validateAndApplyCustomRange();
    };

    const handleCustomToBlur = () => {
        validateAndApplyCustomRange();
    };

    const getTodayString = () => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    };

    return (
        <VStack align="stretch" gap={3}>
            {/* Custom Date Range */}
            <Box>
                <VStack align="stretch" gap={2}>
                    <Box>
                        <Text fontSize="xs" color="gray.500" mb={1}>
                            {t('filter_from') || 'From'}
                        </Text>
                        <Input
                            type="date"
                            size="sm"
                            value={customFrom}
                            onChange={handleCustomFromChange}
                            onBlur={handleCustomFromBlur}
                            max={getTodayString()}
                            placeholder="dd/mm/yyyy"
                            dir={isRTL ? 'rtl' : 'ltr'}
                        />
                    </Box>
                    <Box>
                        <Text fontSize="xs" color="gray.500" mb={1}>
                            {t('filter_to') || 'To'}
                        </Text>
                        <Input
                            type="date"
                            size="sm"
                            value={customTo}
                            onChange={handleCustomToChange}
                            onBlur={handleCustomToBlur}
                            max={getTodayString()}
                            placeholder="dd/mm/yyyy"
                            dir={isRTL ? 'rtl' : 'ltr'}
                        />
                    </Box>
                </VStack>
            </Box>

            {/* Error Message */}
            {error && (
                <Text fontSize="xs" color="red.500" mt={1}>
                    {error}
                </Text>
            )}
        </VStack>
    );
};

